import { Cookies } from 'react-cookie';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

class CookieStorage {
    constructor() {
        this.cookies = new Cookies();
        // Capacitor bridge available (Preferences usable)
        this.isNative = Capacitor.getPlatform() !== 'web';
        // Running inside the RoscoLive mobile app even if the bridge isn't injected on this remote page:
        // the app appends "RoscoLiveApp/<platform>" to the WebView User-Agent (capacitor.config.js)
        this.isApp = this.isNative || /RoscoLiveApp\//.test(navigator.userAgent);
    }

    // options.persist === false keeps the value only for the current app run (Remember Me unchecked):
    // in the app that means sessionStorage, which is cleared when the WebView is killed, instead of
    // Preferences (bridge) or a cookie (no bridge: WebView may keep session cookies across restarts)
    // Otherwise (Remember Me checked) the value is written to Preferences when the bridge is there AND
    // to a cookie with the requested maxAge: Preferences.set can fail on a remote page and the error
    // was swallowed, which silently lost the login on restart. getValue reads either one back.
    async setValue(key, value, options = {}) {
        const { persist, ...cookieOptions } = options;
        if (this.isApp && persist === false) {
            this.setSessionValue(key, value);
            this.cookies.remove(key, { path: '/' });
            if (this.isNative) await this.removePreference(key);
            return;
        }
        if (this.isApp) this.removeSessionValue(key);
        if (this.isNative) {
            try {
                await Preferences.set({ key, value: String(value) });
            } catch (err) {
                console.warn(`Preferences.set failed for ${key}, relying on cookie:`, err);
            }
        }
        this.cookies.set(key, value, {
            path: '/',
            secure: true,
            sameSite: 'strict',
            ...cookieOptions,
        });
    }

    async getValue(key) {
        if (this.isNative) {
            try {
                const { value } = await Preferences.get({ key });
                if (value) return value;
            } catch (err) {
                console.warn(`Preferences.get failed for ${key}:`, err);
            }
        }
        return this.cookies.get(key) || (this.isApp ? this.getSessionValue(key) : null);
    }

    async removeValue(key) {
        if (this.isNative) await this.removePreference(key);
        this.cookies.remove(key, { path: '/' });
        if (this.isApp) this.removeSessionValue(key);
    }

    async removePreference(key) {
        try {
            await Preferences.remove({ key });
        } catch (err) {
            console.warn(`Preferences.remove failed for ${key}:`, err);
        }
    }

    // sessionStorage can throw in restricted WebViews; a failure just means the value isn't kept
    sessionAction(action, fallback = null) {
        try {
            return action(sessionStorage);
        } catch (e) {
            return fallback;
        }
    }

    setSessionValue(key, value) {
        this.sessionAction((storage) => storage.setItem(key, String(value)));
    }

    getSessionValue(key) {
        return this.sessionAction((storage) => storage.getItem(key));
    }

    removeSessionValue(key) {
        this.sessionAction((storage) => storage.removeItem(key));
    }
}

export default CookieStorage;
